import { useContext, useMemo, useState } from 'react';
import { Box, SelectChangeEvent } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { Device } from '../../../../../models/UserDevicesModels';
import UserDetailsContext from '../../../../../contexts/UserDetailsContext/UserDetailsContext';

import { IccidLength } from '../../../../../configs/RegexConfig';

import TextInput from '../../../../../components/TextInput/TextInput';
import Button from '../../../../../components/Button/Button';
import ButtonGroup from '../../../../../components/ButtonGroup/ButtonGroup';
import Dropdown from '../../../../../components/Dropdown/Dropdown';
import useAssignSim from './useAssignSim';
import Alert from '../../../../../components/Alert/Alert';

interface AssignSimStepProps {
  onClose: () => void;
  onAssignSimSuccess: () => void;
  device?: Device | null;
}
function AssignSimStep({ onAssignSimSuccess, onClose, device: deviceFromProps }: AssignSimStepProps) {
  const [t] = useTranslation();
  const user = useContext(UserDetailsContext);
  const { isLoading, assignSim, availableDevices } = useAssignSim(onClose, onAssignSimSuccess, user?.id);

  const [selectedDevice, setSelectedDevice] = useState<Device | null>(deviceFromProps || null);

  const simTypeOptions = ['ESIM', 'PSIM'].map((dev) => ({
    key: dev,
    value: dev,
    label: t(`common.simType.${dev}`),
  }));

  /**
   * Helper function to check if phone number is valid
   * @param value
   */
  const validatePhoneNumber = (value?: string | null) => {
    if (!value) return true;
    const phoneNumber = parsePhoneNumberFromString(value, 'GB');
    return phoneNumber ? phoneNumber.isValid() : false;
  };

  const formik = useFormik({
    initialValues: {
      simType: '',
      deviceId: deviceFromProps?.id || '',
      iccid: '',
      mobileNumber: '',
    },
    validateOnMount: true,
    validateOnChange: true,
    enableReinitialize: true,
    validationSchema: yup.object({
      simType: yup
        .string()
        .required(t('common.inputValidations.requiredField'))
        .test('deviceHasEid', t('common.inputValidations.deviceHasNoEid'), (val, context) => {
          if (val === 'PSIM') return true;
          const { deviceId } = context.parent;
          if (!deviceId) return true;
          const device = deviceFromProps ?? availableDevices.find((d) => d.id === deviceId);
          return !!device?.eid;
        }),
      deviceId: yup.string().required(t('common.inputValidations.requiredField')),
      iccid: yup.string().when('simType', {
        is: (val: string) => val === 'PSIM',
        then: () =>
          yup
            .string()
            .required(t('common.inputValidations.requiredField'))
            .max(IccidLength, t('common.inputValidations.maxLength', { len: IccidLength }))
            .matches(/^[0-9]+$/, t('common.inputValidations.onlyNumbers')),
        otherwise: () =>
          yup
            .string()
            .notRequired()
            .max(IccidLength, t('common.inputValidations.maxLength', { len: IccidLength }))
            .matches(/^[0-9]+$/, t('common.inputValidations.onlyNumbers')),
      }),
      mobileNumber: yup.string().when('simType', {
        is: (val: string) => val === 'PSIM',
        then: () =>
          yup
            .string()
            .required(t('common.inputValidations.requiredField'))
            .test('is-valid-phone', t('common.inputValidations.invalidPhone'), validatePhoneNumber),
        otherwise: () =>
          yup.string().test('is-valid-phone', t('common.inputValidations.invalidPhone'), validatePhoneNumber),
      }),
    }),
    onSubmit: (values) => {
      assignSim({
        userId: user?.id ? user.id : null,
        deviceId: values.deviceId,
        simType: values.simType as 'ESIM' | 'PSIM',
        iccid: values.iccid || null,
        phoneNumber: values.mobileNumber || null,
        eid: values.simType === 'ESIM' && selectedDevice?.eid ? selectedDevice.eid : null,
      });
    },
  });

  const filteredDeviceOptions = useMemo(() => {
    const devices =
      formik.values.simType === 'ESIM' ? availableDevices.filter((device) => Boolean(device.eid)) : availableDevices;

    return devices.map((device) => ({
      value: device.id,
      label: `${device.manufacturer} ${device.model}`,
      key: device.id,
    }));
  }, [availableDevices, formik.values.simType]);

  /**
   * Handle sim type change
   */
  const handleSimTypeChange = async (e: SelectChangeEvent<unknown>) => {
    const nextSimType = e.target.value as string;
    formik.handleChange(e);

    if (!deviceFromProps && formik.values.deviceId) {
      const isCurrentDeviceValid =
        nextSimType !== 'ESIM' || !!availableDevices.find((device) => device.id === formik.values.deviceId)?.eid;

      if (!isCurrentDeviceValid) {
        formik.setFieldValue('deviceId', '');
        setSelectedDevice(null);
      }
    }

    await formik.validateField('simType');
    formik.setFieldTouched('simType', true);
  };

  /**
   * Handle device change
   */
  const handleDeviceChange = async (e: SelectChangeEvent<unknown>) => {
    formik.handleChange(e);
    await formik.validateField('simType');
    setSelectedDevice(availableDevices.find((device) => device.id === e.target.value) || null);
  };

  /**
   * Verify warning visibility condition
   */
  const showAlert = () => {
    const device = deviceFromProps ?? selectedDevice;

    return Boolean(device && device.eid && !device.simId && formik.values.simType === 'PSIM');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <form onSubmit={formik.handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Dropdown
          id="simType"
          name="simType"
          label={t('pages.assignSim.simType')}
          value={formik.values.simType}
          onChange={handleSimTypeChange}
          onBlur={formik.handleBlur}
          error={formik.touched.simType && Boolean(formik.errors.simType)}
          feedback={formik.touched.simType && Boolean(formik.errors.simType) ? formik.errors.simType : ''}
          formStyle={{ paddingBottom: '1.5rem' }}
          items={simTypeOptions}
          placeholder={t('common.placeholders.dropdownPlaceholder') + t('pages.assignSim.simType').toLowerCase()}
        />
        {formik.values.simType && (
          <>
            {!deviceFromProps ? (
              <Dropdown
                id="deviceId"
                name="deviceId"
                label={t('pages.assignSim.device')}
                value={formik.values.deviceId}
                onChange={handleDeviceChange}
                onBlur={formik.handleBlur}
                error={formik.touched.deviceId && Boolean(formik.errors.deviceId)}
                feedback={formik.touched.deviceId && Boolean(formik.errors.deviceId) ? formik.errors.deviceId : ''}
                formStyle={{ paddingBottom: '1.5rem' }}
                items={filteredDeviceOptions}
                placeholder={t('common.placeholders.dropdownPlaceholder') + t('pages.assignSim.device').toLowerCase()}
              />
            ) : null}
            <TextInput
              autoComplete="off"
              key="iccid"
              id="iccid"
              name="iccid"
              type="text"
              label={
                t(`pages.assignSim.iccid`) + (formik.values.simType === 'ESIM' ? t('common.placeholders.optional') : '')
              }
              value={formik.values.iccid}
              onInputChange={(e) => {
                formik.setFieldTouched('iccid', true);
                formik.setFieldValue('iccid', e.target.value);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.iccid && Boolean(formik.errors.iccid)}
              feedback={formik.touched.iccid && Boolean(formik.errors.iccid) ? formik.errors.iccid || '' : ''}
              formStyle={{ paddingBottom: '1.5rem' }}
              placeholder={
                t('common.placeholders.inputPlaceholder') +
                t(`pages.assignSim.iccid`).toLowerCase() +
                (formik.values.simType === 'ESIM' ? t('common.placeholders.optional') : '')
              }
            />
            <TextInput
              autoComplete="off"
              key="mobileNumber"
              id="mobileNumber"
              name="mobileNumber"
              type="text"
              label={
                t(`pages.assignSim.mobileNumber`) +
                (formik.values.simType === 'ESIM' ? t('common.placeholders.optional') : '')
              }
              value={formik.values.mobileNumber}
              onInputChange={(e) => {
                formik.setFieldTouched('mobileNumber', true);
                formik.setFieldValue('mobileNumber', e.target.value);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.mobileNumber && Boolean(formik.errors.mobileNumber)}
              feedback={
                formik.touched.mobileNumber && Boolean(formik.errors.mobileNumber)
                  ? formik.errors.mobileNumber || ''
                  : ''
              }
              formStyle={{ paddingBottom: '1.5rem' }}
              placeholder={
                t('common.placeholders.inputPlaceholder') +
                t(`pages.assignSim.mobileNumber`).toLowerCase() +
                (formik.values.simType === 'ESIM' ? t('common.placeholders.optional') : '')
              }
            />

            {showAlert() && (
              <Box sx={{ marginBottom: '1rem' }}>
                <Alert
                  alertType="info"
                  message={<Trans>{t('pages.assignSim.assignSimWarning')}</Trans>}
                  fontSize="0.875rem"
                />
              </Box>
            )}
          </>
        )}
        <ButtonGroup sx={{ marginTop: 'auto' }}>
          <Button label={t('common.cancel')} color="alternative2Button" onClick={onClose} disabled={isLoading} />
          <Button
            label={t('pages.assignSim.title')}
            color="primaryButton"
            type="submit"
            disabled={!formik.isValid || isLoading || sessionStorage.getItem('impersonateId') !== null}
          />
        </ButtonGroup>
      </form>
    </Box>
  );
}

export default AssignSimStep;
